// Task 206 preview toggles: client-only show/hide overrides per element, never persisted to options or the DB.
// URL form (canvas URL param `vis`, see canvas-url-state.js): "<dcId>~<id>.s,<id>.h".
export const PREVIEW_SHOW = "show";
export const PREVIEW_HIDE = "hide";
export const MAX_PREVIEW_OVERRIDES = 200;

const VERB_TO_CODE = { [PREVIEW_SHOW]: "s", [PREVIEW_HIDE]: "h" };
const CODE_TO_VERB = { s: PREVIEW_SHOW, h: PREVIEW_HIDE };
const ID_PATTERN = /^\d+$/;

export function encodeVisibilityPreview(dc, overrides) {
  const ids = Object.keys(overrides ?? {}).filter(id => VERB_TO_CODE[overrides[id]]);
  ids.sort((a, b) => Number(a) - Number(b));
  if (!dc || ids.length === 0) {
    return "";
  }
  return `${dc}~${ids.map(id => `${id}.${VERB_TO_CODE[overrides[id]]}`).join(",")}`;
}

export function decodeVisibilityPreview(raw, currentDc) {
  const empty = { dc: null, overrides: {}, problems: [] };
  if (raw === "" || raw === null || raw === undefined) {
    return empty;
  }
  const [dcPart, list, ...rest] = String(raw).split("~");
  const dc = Number(dcPart);
  if (!Number.isInteger(dc) || dc <= 0 || list === undefined || rest.length) {
    return { ...empty, problems: [`"${raw}" is not "<page id>~<element id>.s|h,…"; nothing applied`] };
  }
  if (currentDc !== undefined && dc !== Number(currentDc)) {
    return { dc, overrides: {}, problems: [`the preview is for page ${dc} but page ${currentDc} is open; ignored`] };
  }
  const overrides = {};
  const problems = [];
  for (const entry of list.split(",").filter(Boolean)) {
    const [id, code, ...extra] = entry.split(".");
    if (!ID_PATTERN.test(id) || !CODE_TO_VERB[code] || extra.length) {
      problems.push(`dropped "${entry}" (expected <element id>.s or <element id>.h)`);
      continue;
    }
    overrides[id] = CODE_TO_VERB[code];
  }
  return { dc, overrides, problems };
}

export function sanitizePreviewPacket(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const dc = Number(raw.dc);
  if (!Number.isInteger(dc) || dc <= 0) {
    return null;
  }
  const entries = Object.entries(raw.overrides && typeof raw.overrides === "object" ? raw.overrides : {});
  const valid = entries.filter(([id, verb]) => ID_PATTERN.test(id) && VERB_TO_CODE[verb]);
  const kept = valid.slice(0, MAX_PREVIEW_OVERRIDES);
  return { dc, overrides: Object.fromEntries(kept), dropped: entries.length - kept.length };
}

// Toggling to the element's own state clears the override rather than pinning it.
export function overrideForToggle(baseVisible, show) {
  if (Boolean(show) === Boolean(baseVisible)) {
    return null;
  }
  return show ? PREVIEW_SHOW : PREVIEW_HIDE;
}

export function applyPreviewOverride(override, baseVisible) {
  if (override === PREVIEW_SHOW) {
    return true;
  }
  if (override === PREVIEW_HIDE) {
    return false;
  }
  return baseVisible;
}

// Follow mode: adopt the leader's overrides wholesale, only for the page this client has open.
export function planPreviewAdoption(preview, { loadedDc, currentOverrides }) {
  if (!preview || preview.dc !== Number(loadedDc)) {
    return { action: "ignore" };
  }
  const same = encodeVisibilityPreview(preview.dc, preview.overrides) === encodeVisibilityPreview(preview.dc, currentOverrides);
  return same ? { action: "ignore" } : { action: "apply", overrides: { ...preview.overrides } };
}
